import { get, send } from "../api.js";

const storageKey = "wmf-message-alert-device-v1";
type Binding = { deviceKey: string; accountId: string | null };
export type PreparedPush = { state: "on" | "off"; registration: ServiceWorkerRegistration; subscription: PushSubscription | null; publicKey: string; deviceKey: string };
export type PushPreparation = PreparedPush | { state: "unsupported" | "install" | "blocked" | "unconfigured" | "update" };
let activeAccount: string | null = null, generation = 0;
let cleanup: Promise<void> = Promise.resolve();

function binding(): Binding | null {
  try { const value = JSON.parse(localStorage.getItem(storageKey) ?? "null") as Binding | null;
    return value && /^[A-Za-z0-9_-]{43}$/.test(value.deviceKey) ? value : null;
  } catch { return null; }
}
function store(value: Binding) { localStorage.setItem(storageKey, JSON.stringify(value)); }
function deviceKey(): string {
  const existing = binding(); if (existing) return existing.deviceKey;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const key = btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  store({ deviceKey: key, accountId: null }); return key;
}

/** A new signed-in account must explicitly opt in again on this browser. */
export function setMessagePushAccount(accountId: string | null): void {
  // A 401 means the session needs renewal, not that this person opted out.
  // Invalidate in-flight opt-in, but retain the saved device preference.
  if (accountId === null) {
    if (activeAccount !== null) { activeAccount = null; generation++; }
    return;
  }
  if (activeAccount === accountId && (!binding()?.accountId || binding()?.accountId === accountId)) return;
  activeAccount = accountId; generation++;
  const previous = binding();
  if (!previous?.accountId || previous.accountId === accountId) return;
  void clearMessagePushAccount();
  activeAccount = accountId;
}

/** Only explicit sign-out or a confirmed different account discards this binding. */
export function clearMessagePushAccount(): Promise<void> {
  activeAccount = null; generation++;
  const previous = binding();
  if (previous) try { store({ deviceKey: previous.deviceKey, accountId: null }); } catch { /* Never interrupt sign-in or sign-out. */ }
  cleanup = cleanup.catch(() => {}).then(async () => {
    if (!("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration();
    registration?.active?.postMessage({ type: "WMF_CLEAR_MESSAGE_ALERTS" });
    await (await registration?.pushManager?.getSubscription())?.unsubscribe();
  }).catch(() => { /* The server also disables subscriptions on explicit sign-out. */ });
  return cleanup;
}

function support(): "supported" | "unsupported" | "install" {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (ios && !standalone) return "install";
  return window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window ? "supported" : "unsupported";
}

async function registrationReady(): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) return existing;
  let timer: ReturnType<typeof setTimeout>;
  try { return await Promise.race([navigator.serviceWorker.ready, new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 8000); })]); }
  finally { clearTimeout(timer!); }
}
async function workerReady(registration: ServiceWorkerRegistration): Promise<boolean> {
  return new Promise(resolve => {
    const channel = new MessageChannel();
    const done = (ready: boolean) => { clearTimeout(timer); channel.port1.close(); channel.port2.close(); resolve(ready); };
    const timer = setTimeout(() => done(false), 1500);
    channel.port1.onmessage = event => done(event.data?.version === 1);
    registration.active?.postMessage({ type: "WMF_MESSAGE_PUSH_READY" }, [channel.port2]);
  });
}
function applicationKey(value: string): Uint8Array<ArrayBuffer> {
  const encoded = value.replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(encoded + "=".repeat((4 - encoded.length % 4) % 4)), c => c.charCodeAt(0));
}

export async function prepareMessagePush(accountId: string): Promise<PushPreparation> {
  const supported = support(); if (supported === "unsupported") return { state: supported };
  setMessagePushAccount(accountId); await cleanup;
  const config = await get<{ configured: boolean; publicKey: string | null }>("/message-push/config");
  if (!config.configured || !config.publicKey) return { state: "unconfigured" };
  if (supported === "install") return { state: "install" };
  if (Notification.permission === "denied") return { state: "blocked" };
  const registration = await registrationReady();
  if (!registration || !await workerReady(registration)) return { state: "update" };
  let subscription = await registration.pushManager.getSubscription();
  const key = deviceKey();
  if (subscription) {
    const serverKey = subscription.options.applicationServerKey;
    const expected = applicationKey(config.publicKey);
    const sameKey = serverKey && new Uint8Array(serverKey).every((value, index) => value === expected[index]) && serverKey.byteLength === expected.length;
    if (!sameKey || binding()?.accountId !== accountId) { await subscription.unsubscribe(); subscription = null; }
  }
  const status = subscription ? await send<{ enabled: boolean }>("POST", "/message-push/status", { deviceKey: key, endpoint: subscription.endpoint }) : { enabled: false };
  return { state: status.enabled && Notification.permission === "granted" ? "on" : "off", registration, subscription, publicKey: config.publicKey, deviceKey: key };
}

export async function enableMessagePush(accountId: string, prepared: PreparedPush): Promise<void> {
  const attempt = generation;
  // All preparation is complete before the tap: subscribe runs directly in the user gesture on iOS.
  const requested = prepared.subscription ? Promise.resolve(prepared.subscription) : prepared.registration.pushManager.subscribe({
    userVisibleOnly: true, applicationServerKey: applicationKey(prepared.publicKey),
  });
  const subscription = await requested;
  if (activeAccount !== accountId || generation !== attempt) { await subscription.unsubscribe(); throw new Error("Your account changed. Enable alerts again."); }
  try {
    await send("POST", "/message-push/subscription", { accountId, deviceKey: prepared.deviceKey, publicKey: prepared.publicKey, subscription: subscription.toJSON() });
    if (activeAccount !== accountId || generation !== attempt) throw new Error("Your account changed. Enable alerts again.");
    store({ deviceKey: prepared.deviceKey, accountId });
  } catch (cause) {
    await subscription.unsubscribe().catch(() => {});
    throw cause;
  }
}

export async function disableMessagePush(accountId: string, prepared: PreparedPush): Promise<void> {
  await send("POST", "/message-push/disable", { accountId, deviceKey: prepared.deviceKey });
  prepared.registration.active?.postMessage({ type: "WMF_CLEAR_MESSAGE_ALERTS" });
  await prepared.subscription?.unsubscribe();
  if (binding()?.accountId === accountId) store({ deviceKey: prepared.deviceKey, accountId: null });
}
